import { useState, useEffect } from 'react';
import { CelebrationModal, triggerConfetti } from './Celebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier colors
const TIER_COLORS = {
  gold: 'from-yellow-400 to-amber-500',
  silver: 'from-gray-300 to-gray-400',
  bronze: 'from-orange-400 to-orange-600'
};

const TIER_BG = {
  gold: 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300',
  silver: 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300',
  bronze: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300'
};

// Badge Card Component
export function BadgeCard({ badge, size = 'normal' }) {
  const isLarge = size === 'large';
  const tierColor = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;
  const tierBg = TIER_BG[badge.tier] || TIER_BG.bronze;
  
  return (
    <div 
      className={`relative rounded-xl border-2 transition-all ${tierBg} ${
        badge.earned 
          ? 'opacity-100 shadow-md hover:shadow-lg' 
          : 'opacity-40 grayscale'
      } ${isLarge ? 'p-6' : 'p-4'}`}
    >
      {/* Tier ribbon */}
      {badge.earned && (
        <div className={`absolute -top-1 -right-1 w-8 h-8 bg-gradient-to-br ${tierColor} rounded-full flex items-center justify-center shadow-md`}>
          {badge.tier === 'gold' && '⭐'}
          {badge.tier === 'silver' && '✨'}
          {badge.tier === 'bronze' && '🌟'}
        </div>
      )}
      
      <div className="text-center">
        <div className={`${isLarge ? 'text-5xl' : 'text-3xl'} mb-2`}>
          {badge.emoji}
        </div>
        <h3 className={`font-bold text-gray-900 ${isLarge ? 'text-lg' : 'text-sm'}`}>
          {badge.name}
        </h3>
        <p className={`text-gray-600 ${isLarge ? 'text-sm mt-2' : 'text-xs mt-1'}`}>
          {badge.description}
        </p>
        {badge.earned && badge.earned_at && (
          <p className="text-xs text-green-600 mt-2">
            ✓ Earned {new Date(badge.earned_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}

// Badge Notification Toast
export function BadgeNotification({ badge, onClose }) {
  useEffect(() => {
    triggerConfetti();
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className="bg-white rounded-xl shadow-2xl border-2 border-yellow-400 p-4 max-w-sm">
        <div className="flex items-center gap-3">
          <div className="text-4xl animate-bounce">{badge.emoji}</div>
          <div>
            <p className="text-sm text-yellow-600 font-bold uppercase">Badge Earned!</p>
            <p className="font-bold text-gray-900">{badge.name}</p>
            <p className="text-xs text-gray-600">{badge.description}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// Badge Progress Bar
export function BadgeProgress({ earned, total }) {
  const percentage = total > 0 ? (earned / total) * 100 : 0;
  
  return (
    <div className="w-full">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">Badge Progress</span>
        <span className="font-bold text-gray-900">{earned}/{total}</span>
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Hook to check and award badges
export function useBadgeChecker() {
  const [newBadge, setNewBadge] = useState(null);
  
  const checkBadges = async () => {
    try {
      const response = await fetch(`${API}/badges/check`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.newly_earned && data.newly_earned.length > 0) {
          // Show the first newly earned badge
          setNewBadge(data.newly_earned[0]);
        }
        return data;
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
    return null;
  };
  
  const dismissBadge = () => setNewBadge(null);
  
  return { checkBadges, newBadge, dismissBadge };
}

// Badges Display Grid
export function BadgesGrid({ badges, showLocked = true }) {
  const displayBadges = showLocked ? badges : badges.filter(b => b.earned);
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {displayBadges.map(badge => (
        <BadgeCard key={badge.id} badge={badge} />
      ))}
    </div>
  );
}

// Mini badges row for dashboard
export function BadgesMini({ limit = 5 }) {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadBadges = async () => {
      try {
        const response = await fetch(`${API}/badges/recent`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setBadges(data.badges || []);
        }
      } catch (error) {
        console.error('Error loading badges:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBadges();
  }, []);
  
  if (loading || badges.length === 0) return null;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Recent badges:</span>
      <div className="flex -space-x-1">
        {badges.slice(0, limit).map((badge, i) => (
          <div 
            key={badge.id}
            className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200 flex items-center justify-center border-2 border-white shadow-sm"
            title={badge.name}
          >
            <span className="text-lg">{badge.emoji}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BadgesGrid;
